import { Box, Grid, Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useMemo } from 'react';
import CommitRoundedIcon from '@mui/icons-material/CommitRounded';
import { parseTabData } from '../../../../../utils/helpers';
import Chart from '../../../../_common/VisxChart';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgb(10, 25, 41)' : '#fff',
  ...theme.typography.body2,
  position: 'relative',
  color: theme.palette.text.secondary,
  boxShadow: theme.palette.mode === 'dark' ? 0 : '0 0.5px 1px rgb(255 255 255 / 20%), 0 2px 3px rgb(255 255 255 / 10%)',
  border: theme.palette.mode === 'dark' ? '1px solid rgb(19, 47, 76)' : '1px solid #eae9ee',
  borderRadius: '7px',
  '& .Item-Head': {
    background: '#f2f2f2',
    borderBottom: '1px solid #eae9ee',
    padding: '0 8px 0 8px',
    borderRadius: '6px 6px 0 0',
    '& a': {
      textDecoration: 'none',
    },
    '& .option-title': {
      fontWeight: 500,
      color: '#333',
      fontSize: '.875rem',
    }
  },
  '& .Item-Body': {
    padding: theme.spacing(1),
    paddingTop: 0,
  }
}));

const getChartData = (chart, ticker) => {
  if (!chart || !chart.series) return chart;

  return {
    ...chart,
    series: chart.series.map(d => ({
      ...d,
      yformula: d.yformula ? d.yformula.replace(/AAPL/g, ticker) : d.yformula
    })),
    ySeries: chart.ySeries ? chart.ySeries.replace(/AAPL/g, ticker) : chart.ySeries
  };
};

const RSIChart = ({ tabData, info, themes, chart_state, desk }) => {

  const { rsiItem, rsiSettings, dataToFilterSet } = useMemo(() => {
    const rawRsiItem = tabData?.find(d => d.section_code === "rsi");
    if (!rawRsiItem) return { rsiItem: null, rsiSettings: null, dataToFilterSet: new Set() };

    const parsedItem = parseTabData(rawRsiItem);
    const settings = parsedItem?.settings || {};

    const filters = (settings.dataToFilter || []).map(d => d.replace(/XXXXX/g, info?.ticker || ''));
    return { rsiItem: parsedItem, rsiSettings: settings, dataToFilterSet: new Set(filters) };
  }, [tabData, info?.ticker]);

  const rsiSeriesData = useMemo(() => {
    if (dataToFilterSet.size === 0) return null;

    const rawIsSeries = tabData?.find(d => d.section_code === "ISSeries");
    if (!rawIsSeries) return null;

    const parsedSeries = parseTabData(rawIsSeries);
    const filteredTabData = parsedSeries?.tabData?.filter(d => dataToFilterSet.has(d.code)) || [];

    return filteredTabData.length > 0 ? filteredTabData : null;
  }, [tabData, dataToFilterSet]);

  const transformedData = useMemo(() => {
    return rsiSeriesData?.map(item => ({
      date: new Date(item.recordDate),
      value: item.value,
    })) || [];
  }, [rsiSeriesData]);

  const chartData = useMemo(() => {
    const baseChartData = getChartData(rsiItem?.tabData, info?.ticker) || {};
    return {
      ...baseChartData,
      id: rsiItem?.section_code || 'risk_Drawdown_chart',
      tick: 2,
      series_data: transformedData
    };
  }, [rsiItem, info?.ticker, transformedData]);

  if (!rsiItem || !rsiSeriesData || !chartData.series) {
    return null;
  }

  const formattedSectionName = rsiItem?.section_name?.replace(/XXXXX/g, info?.ticker || '') || '';
  const formattedDescription = rsiSettings?.desc?.replace(/XXXXX/g, info?.companyName || '') || '';

  return (
    <Grid size={{ md: 12, xs: 12 }}>
      <Item className="crisischart">
        <Box className="Item-Head"
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            mb: 0.5,
            px: 0.5,
            '& a': { textDecoration: 'none', fontSize: '.875rem', textTransform: 'capitalize', background: '#f0f1f4', fontWeight: 500 },
            '& a:hover': {
              background: '#e6e7eb',
              border: '1px solid #dbdce4',
            },
          }}
        >
          <Typography component="p" className="option-title" sx={{ mr: 'auto' }}>
            {info?.ticker} {formattedSectionName}
          </Typography>
        </Box>
        <Box component="div" className="option-content Item-Body">
          <Box component="div" className="life-story-chart" sx={{ width: '100%', height: "100%", pt: 1, minHeight: { md: '8rem', xs: '6.3rem' } }}>
            <Chart
              data={chartData}
              _height={desk ? 120 : 120}
              width={250}
              _margin={{ top: 10, right: 30, bottom: 25, left: 10 }}
              threshold={[70, 30]}
              isRSI={true}
            />
          </Box>
          <Box component="div" sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
            {chartData.series.map((d, i) => {
              if (!d?.legend?.label) return null;
              return (
                <Box key={`legend-${d.legend.label}-${i}`} component="div" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: .4, mr: 1.5 }}>
                  <CommitRoundedIcon aria-hidden="true" sx={{ mr: .7, width: '1em !important', fontSize: '1.1rem !important', color: d?.setting?.line?.stroke }} />
                  <Typography variant="body2" sx={{ fontSize: '.67rem', fontWeight: 600 }}>{d.legend.label}</Typography>
                </Box>
              );
            })}
          </Box>
          {formattedDescription && (
            <Typography
              component="div"
              variant="caption"
              className="option-title"
              sx={{ pb: 2, px: 2 }}
              dangerouslySetInnerHTML={{ __html: formattedDescription }}
            />
          )}
        </Box>
      </Item>
    </Grid>
  );
};

export default React.memo(RSIChart);